import React, { useMemo } from 'react';

import { Container, Row, Col, Table, Spinner } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';

import { TablePagination } from '../../../components';

import './styles.scoped.css';

const getSortedIcon = (isSorted, isSortedDesc) => {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
};

export const wrapOnsitePayrollColumns = [
  {
    Header: 'Checks Paid From Date',
    accessor: 'FromPayrollCalcDate',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Checks Paid To Date',
    accessor: 'ToPayrollCalcDate',
    className: 'align-middle text-nowrap',
  },
    {
    Header: 'W Proj Project Number',
    accessor: 'WProjNumber',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Proj Project Full Name',
    accessor: 'WProjFullName',
    className: 'align-middle text-nowrap',
  },
    {
    Header: 'W SubProj Number',
    accessor: 'WSubProjNumber',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W SubProj Name',
    accessor: 'WSubProjName',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Client Number',
    accessor: 'CustId',
    className: 'align-middle text-nowrap',
  },
    {
    Header: 'Client Name',
    accessor: 'CustName',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Job Order Number',
    accessor: 'JobOrderNumber',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Corp RM Policy Number',
    accessor: 'WCorpRMPolicyNbr',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Proj General Contractor Name',
    accessor: 'WProjGeneralContractorName',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Corp RM Enrollment Contract Number',
    accessor: 'WCorpRMEnrollmentContractNbr',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Corp RM Wrapup Number',
    accessor: 'WCorpRMWrapupNbr',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'First Date Paid',
    accessor: 'FirstDatePaid',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Last Date Paid',
    accessor: 'LastDatePaid',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Job Site State',
    accessor: 'JobSiteState',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'W Proj Coverage Type',
    accessor: 'WProjCoverageType',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Comp Code',
    accessor: 'CompCode',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Workers Comp Description',
    accessor: 'WorkersCompDescription',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'GL Classification Description',
    accessor: 'GLClassificationDescription',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'GL Code',
    accessor: 'GLCode',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Regular Hours',
    accessor: 'RegularHours',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Overtime Hours',
    accessor: 'OvertimeHours',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Doubletime Hours',
    accessor: 'DoubletimeHours',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Total Hours Worked',
    accessor: 'TotalHoursWorked',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Regular Wages',
    accessor: 'RegularWages',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Overtime Wages',
    accessor: 'OvertimeWages',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Doubletime Wages',
    accessor: 'DoubletimeWages',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Total Gross Wages',
    accessor: 'TotalGrossWages',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Total Straight Wages',
    accessor: 'TotalStraightWages',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Total Receipts',
    accessor: 'TotalReceipts',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Cumulative Project Hours',
    accessor: 'CumulativeProjectHours',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Cumulative Project WRAP Payroll',
    accessor: 'CumulativeProjectWRAPPayroll',
    className: 'align-middle text-nowrap',
  },
  {
    Header: 'Cumulative Project Receipts',
    accessor: 'CumulativeProjectReceipts',
    className: 'align-middle text-nowrap',
  },
];

export const WrapOnsitePayrollReportTable = ({ data, loading }) => {
  const memoData = useMemo(() => data, [data]);

  const tableInstance = useTable(
    {
      columns: wrapOnsitePayrollColumns,
      data: memoData,
      initialState: {
        pageSize: 200,
        sortBy: [
          {
            id: 'JobOrderNumber',
            desc: false,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};
